import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";

const FOUNDER_LINKEDIN =
  "https://www.linkedin.com/in/chhavi-kalra-151326332";

const standard = [
  {
    n: "01",
    title: "Source it",
    body: "Important claims should be grounded in reliable, verifiable sources wherever possible.",
  },
  {
    n: "02",
    title: "Separate fact from interpretation",
    body: "Information, analysis, and opinion should not be presented as the same thing.",
  },
  {
    n: "03",
    title: "Make it practical",
    body: "Resources should help someone actually understand or prepare for the work — not simply add more information.",
  },
  {
    n: "04",
    title: "Be honest about uncertainty",
    body: "When information is unclear, incomplete, or sources disagree, say so rather than presenting a guess as fact.",
  },
];

const principles = [
  {
    title: "Structured",
    body: "Understand the career before deciding how to prepare for it.",
  },
  {
    title: "Curated",
    body: "Find useful resources without searching endlessly through scattered information.",
  },
  {
    title: "Practical",
    body: "Turn what you learn into preparation you can actually use.",
  },
];

function Eyebrow({ children }) {
  return (
    <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">
      {children}
    </div>
  );
}

function Section({ eyebrow, title, children, testId }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.6 }}
      data-testid={testId}
      className="border-t border-slate-200/70 pt-8 sm:pt-10"
    >
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#0A2540] font-medium mt-3 leading-tight max-w-2xl">
        {title}
      </h2>
      {children}
    </motion.section>
  );
}

export default function About() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "About VantStride | Finance Career Clarity";
    let meta = document.querySelector('meta[name="description"]');
    const prevMeta = meta ? meta.getAttribute("content") : null;
    if (meta) {
      meta.setAttribute(
        "content",
        "Learn why VantStride was built, the principles behind it, and how we're making finance-career exploration and preparation more structured, practical, and transparent."
      );
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevMeta !== null) meta.setAttribute("content", prevMeta);
    };
  }, []);

  return (
    <div
      data-testid="about-page"
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20"
    >
      {/* 1 — Hero */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl"
      >
        <Eyebrow>About</Eyebrow>
        <h1 className="font-display text-4xl sm:text-6xl tracking-tight text-[#0A2540] font-medium mt-3 leading-[1.05]">
          Built by someone who wanted a clearer way into finance.
        </h1>
      </motion.div>

      <div className="mt-10 sm:mt-14 space-y-10 sm:space-y-14">
        {/* 2 — Founder */}
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6 }}
          data-testid="about-founder"
          className="border-t border-slate-200/70 pt-8 sm:pt-10 grid md:grid-cols-12 gap-10 md:gap-12 items-start"
        >
          <div className="md:col-span-7">
            <Eyebrow>Founder of VantStride</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#0A2540] font-medium mt-3 leading-tight">
              Chhavi Kalra
            </h2>
            <div className="mt-1.5 text-sm vestryn-accent font-medium">
              Founder, VantStride
            </div>
            <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
              <p>
                I studied Banking &amp; Financial Markets and built VantStride
                around a problem I experienced myself: trying to understand
                finance careers meant piecing together information from
                countless places — career guides, courses, interviews,
                technical resources, and opinions — without a clear picture of
                what actually mattered.
              </p>
              <p>VantStride started with a simple question:</p>
              <p className="font-display text-lg sm:text-xl text-[#0A2540] leading-snug">
                What if you could understand the path before spending hundreds
                of hours preparing for it?
              </p>
            </div>
            <a
              href={FOUNDER_LINKEDIN}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="founder-linkedin"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#0A2540] group"
            >
              Connect with me on LinkedIn
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-3xl bg-[#0A2540] p-8 sm:p-10 text-white">
              <img
                src="/vs-monogram.png"
                alt=""
                aria-hidden="true"
                className="w-10 h-10 object-contain"
              />
              <p className="mt-6 font-display text-xl sm:text-2xl leading-snug">
                "Clarity first. The journey can wait until the map makes
                sense."
              </p>
              <p className="mt-4 text-xs uppercase tracking-[0.22em] vestryn-accent font-semibold">
                Founder of VantStride
              </p>
            </div>
          </div>
        </motion.section>

        {/* 3 — Why VantStride exists */}
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6 }}
          data-testid="about-why"
          className="border-t border-slate-200/70 pt-8 sm:pt-10 grid md:grid-cols-12 gap-10 md:gap-12 items-start"
        >
          <div className="md:col-span-7">
            <Eyebrow>Why VantStride exists</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#0A2540] font-medium mt-3 leading-tight max-w-2xl">
              Finance careers shouldn't require a scavenger hunt.
            </h2>
            <div className="mt-6 space-y-4 text-slate-600 leading-relaxed max-w-2xl">
              <p>
                You can find information about Investment Banking, Private
                Equity, Venture Capital, Hedge Funds and other finance careers
                almost anywhere.
              </p>
              <p>
                The harder part is knowing what to trust, what to learn first,
                which skills actually matter, and how the pieces fit together.
              </p>
              <p>That&apos;s what VantStride was created to solve.</p>
              <p>
                The goal is simple: give people a clearer map before they commit
                hundreds of hours to a career path — helping them understand the
                work, the preparation, and the resources that can move them
                forward.
              </p>
            </div>
          </div>
          <div className="md:col-span-5">
            <div
              data-testid="about-name-box"
              className="rounded-3xl bg-[#0A2540] p-8 sm:p-10 text-white"
            >
              <div className="text-xs uppercase tracking-[0.22em] font-semibold vestryn-accent">
                The name
              </div>
              <div className="mt-5 space-y-4 text-white/85 leading-relaxed text-[15px]">
                <p>
                  <span className="font-display italic text-white">"Vant"</span>{" "}
                  represents the advantage that comes from understanding a
                  career before stepping into it — knowing what the work
                  involves, what skills matter, and how to prepare.
                </p>
                <p>
                  <span className="font-display italic text-white">"Stride"</span>{" "}
                  represents forward movement: taking that knowledge and turning
                  it into deliberate progress toward your career.
                </p>
                <p>
                  Together, VantStride reflects what the platform is built to
                  provide — an edge before you take your next step.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* 4 — Mission */}
        <Section
          eyebrow="Our mission"
          title="Make finance careers easier to understand before you step into them."
          testId="about-mission"
        >
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed max-w-2xl">
            <p>
              VantStride exists to bring clarity to finance-career exploration
              and preparation.
            </p>
            <p>
              Instead of leaving students and early-career professionals to
              piece together scattered information on their own, VantStride
              aims to make the path easier to understand, the preparation more
              structured, and useful resources easier to find.
            </p>
          </div>
        </Section>

        {/* 5 — The Standard */}
        <Section
          eyebrow="The standard"
          title="We'd rather leave something out than make it up."
          testId="about-standard"
        >
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed max-w-2xl">
            <p>
              VantStride is built around a simple principle: useful career
              information should be grounded, transparent, and practical.
            </p>
            <p className="text-sm uppercase tracking-[0.18em] font-semibold text-slate-500">
              Four principles
            </p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 gap-x-12 gap-y-10">
            {standard.map((p) => (
              <div key={p.n} data-testid={`about-standard-${p.n}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-lg vestryn-accent">
                    {p.n}
                  </span>
                  <h3 className="font-display text-xl text-[#0A2540]">
                    {p.title}
                  </h3>
                </div>
                <p className="mt-2 text-slate-600 leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-slate-600 leading-relaxed max-w-2xl">
            This standard guides the content, resources, Playbooks, tools, and
            VantStride AI we build.
          </p>
        </Section>

        {/* 6 — Philosophy */}
        <Section
          eyebrow="Our philosophy"
          title="Give people the map before asking them to take the journey."
          testId="about-philosophy"
        >
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed max-w-2xl">
            <p>
              A finance career is a long journey. The first step
              shouldn&apos;t be taken blindly.
            </p>
            <p>
              VantStride is built around the belief that better preparation
              starts with better understanding — knowing what a career
              involves, what the path demands, what skills matter, and where
              to go deeper.
            </p>
            <p>
              The aim isn&apos;t to tell everyone which career to choose.
            </p>
            <p>
              It&apos;s to give them enough clarity to make that decision
              deliberately.
            </p>
          </div>
        </Section>

        {/* 7 — What we stand for */}
        <Section
          eyebrow="What we stand for"
          title="Three principles. That's it."
          testId="about-principles"
        >
          <div className="mt-8 grid sm:grid-cols-3 gap-x-10 gap-y-8">
            {principles.map((p) => (
              <div key={p.title} data-testid={`about-principle-${p.title.toLowerCase()}`}>
                <h3 className="font-display text-xl text-[#0A2540] uppercase tracking-wide">
                  {p.title}
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* 8 — Transparency */}
        <Section
          eyebrow="Transparency"
          title="Useful recommendations should be useful first."
          testId="about-transparency"
        >
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed max-w-2xl">
            <p>
              Some resources recommended on VantStride are shared through
              affiliate partnerships.
            </p>
            <p>
              When a link is an affiliate link, we disclose it. Recommendations
              are selected because they can be useful for someone exploring or
              preparing for a finance career — not simply because they generate
              a commission.
            </p>
          </div>
          <Link
            to="/affiliate-disclosure"
            data-testid="about-affiliate-link"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#0A2540] group"
          >
            View our Affiliate Disclosure
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </Section>

        {/* 9 — Closing */}
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          data-testid="about-closing"
          className="border-t border-slate-200/70 pt-8 sm:pt-10"
        >
          <h2 className="font-display text-3xl sm:text-5xl tracking-tight text-[#0A2540] font-medium leading-tight">
            Start with the map.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed max-w-2xl">
            Explore a career path. Understand what it involves. Find the
            resources that matter. Then decide what you want to pursue.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/roadmaps"
              data-testid="about-cta-hubs"
              className="inline-flex items-center gap-2 bg-[#0A2540] text-white rounded-full px-6 py-3.5 text-sm font-medium hover:bg-[#051A2E] transition-colors active:scale-95"
            >
              Explore Career Hubs
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/playbooks"
              data-testid="about-cta-playbooks"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3.5 text-sm font-medium text-[#0A2540] hover:bg-slate-50 transition-colors active:scale-95"
            >
              Explore Playbooks
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-12 space-y-1">
            <p className="font-display text-lg text-[#0A2540]">
              Your Edge, Before Day One.
            </p>
            <p className="font-display text-lg vestryn-accent">
              We Give You the Map First.
            </p>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
